/**
 * Ground hazards that move: bouncing axes, spiralling fangs, tracking beams, chasing blobs,
 * growing pools and linear waves. The zone itself is a normal `Zone` (combat ticks it); this
 * module only places it and moves `zone.area` each tick, keeping `zone.motion.vel` current so
 * bots and renderers can extrapolate.
 */
import type { EffectCtx, HazardMechanic, SimContext, Unit, Vec2, WorldShape, Zone, ZoneMotionSpec, ZoneMotionState } from '../../contracts';
import { TICK_MS } from '../../contracts';
import { angleTo, arenaOf, dist, pickTargets, polar, pushZone, resolveKeyScaled, unitOf, wrapAngle } from './state';

const DT = TICK_MS / 1000;

function initialHeading(ctx: SimContext, src: Unit, target: Unit | undefined, mode: string | undefined, i: number, n: number): number {
  const toTarget = target && target.id !== src.id ? angleTo(src.pos, target.pos) : src.facing;
  switch (mode) {
    case 'facing':
      return src.facing;
    case 'target':
      return toTarget;
    case 'random':
      return ctx.rng.range(-Math.PI, Math.PI);
    default:
      // 'spread': evenly around the aim direction.
      return toTarget + (2 * Math.PI * i) / Math.max(1, n);
  }
}

export function applyHazard(ctx: SimContext, spec: HazardMechanic, ectx: EffectCtx): void {
  const src = unitOf(ctx, ectx.sourceId);
  if (!src) return;
  const target = unitOf(ctx, ectx.targetId);
  const n = Math.max(1, Math.round(resolveKeyScaled(ctx, spec.count, 1)));
  const base = ctx.sys.spatial.resolveArea(ctx.world, spec.zone.shape, spec.zone.anchor, src.id, ectx.targetId, ectx.point);
  const m = spec.motion;
  const intent = spec.intent ?? 'avoid';
  const spiralStart = m?.type === 'spiral' ? ctx.rng.range(-Math.PI, Math.PI) : 0;
  for (let i = 0; i < n; i++) {
    const area: WorldShape = { shape: { ...base.shape }, origin: { ...base.origin }, facing: base.facing };
    let motion: ZoneMotionState | undefined;
    if (m) {
      motion = { spec: m, vel: { x: 0, y: 0 } };
      switch (m.type) {
        case 'bounce':
        case 'linear': {
          const h = initialHeading(ctx, src, target, m.heading ?? (m.type === 'bounce' ? 'spread' : 'target'), i, n);
          motion.vel = { x: Math.cos(h) * m.speed, y: Math.sin(h) * m.speed };
          area.facing = h;
          break;
        }
        case 'spiral': {
          motion.center = { ...base.origin };
          motion.angle = spiralStart + (2 * Math.PI * i) / n;
          motion.dist = m.startRadius ?? 0;
          area.origin = polar(motion.center, motion.angle, motion.dist);
          break;
        }
        case 'aim': {
          const t = pickTargets(ctx, src, m.rule ?? 'randomNotTank', 1, ectx.targetId)[0];
          motion.anchorId = src.id;
          motion.targetId = t?.id ?? null;
          motion.angle = src.facing;
          area.origin = { x: src.pos.x, y: src.pos.y };
          area.facing = src.facing;
          break;
        }
        case 'chase': {
          const t = pickTargets(ctx, src, m.rule ?? 'random', 1, ectx.targetId)[0];
          motion.targetId = t?.id ?? null;
          break;
        }
        case 'grow':
          break;
      }
    }
    pushZone(ctx, src.id, ectx.abilityId, spec.zone, area, intent, motion);
  }
}

/** Is `p` (with radius r) inside the arena rect, if any, and on walkable ground. */
function free(ctx: SimContext, arena: { x: number; y: number; w: number; h: number } | undefined, p: Vec2, r: number): boolean {
  if (arena && (p.x - r < arena.x || p.x + r > arena.x + arena.w || p.y - r < arena.y || p.y + r > arena.y + arena.h)) return false;
  return ctx.sys.spatial.isWalkable(ctx.world, p);
}

function zoneRadius(z: Zone): number {
  const s = z.area.shape;
  return s.type === 'circle' ? s.radius : s.type === 'ring' ? s.outer : 0;
}

function stepBounce(ctx: SimContext, z: Zone, mo: ZoneMotionState): void {
  const src = unitOf(ctx, z.sourceId);
  const arena = arenaOf(ctx, src);
  const r = zoneRadius(z);
  const o = z.area.origin;
  let { x: vx, y: vy } = mo.vel;
  const tryAt = (ax: number, ay: number): Vec2 => ({ x: o.x + ax * DT, y: o.y + ay * DT });
  let next = tryAt(vx, vy);
  if (!free(ctx, arena, next, r)) {
    if (free(ctx, arena, tryAt(-vx, vy), r)) vx = -vx;
    else if (free(ctx, arena, tryAt(vx, -vy), r)) vy = -vy;
    else {
      vx = -vx;
      vy = -vy;
    }
    next = tryAt(vx, vy);
    if (!free(ctx, arena, next, r)) next = { ...o };
  }
  mo.vel = { x: vx, y: vy };
  z.area.origin = next;
  z.area.facing = Math.atan2(vy, vx);
}

function stepMotion(ctx: SimContext, z: Zone, mo: ZoneMotionState): void {
  const m: ZoneMotionSpec = mo.spec;
  switch (m.type) {
    case 'bounce':
      stepBounce(ctx, z, mo);
      return;
    case 'linear':
      z.area.origin = { x: z.area.origin.x + mo.vel.x * DT, y: z.area.origin.y + mo.vel.y * DT };
      return;
    case 'spiral': {
      const c = mo.center ?? z.area.origin;
      const prev = z.area.origin;
      mo.angle = (mo.angle ?? 0) + m.angularSpeed * DT;
      mo.dist = (mo.dist ?? 0) + m.radialSpeed * DT;
      const next = polar(c, mo.angle, mo.dist);
      mo.vel = { x: (next.x - prev.x) / DT, y: (next.y - prev.y) / DT };
      z.area.origin = next;
      return;
    }
    case 'aim': {
      const anchor = unitOf(ctx, mo.anchorId);
      if (anchor?.alive) z.area.origin = { x: anchor.pos.x, y: anchor.pos.y };
      let t = unitOf(ctx, mo.targetId);
      if (!t?.alive && anchor) {
        t = pickTargets(ctx, anchor, m.rule ?? 'randomNotTank', 1, null)[0];
        mo.targetId = t?.id ?? null;
      }
      const cur = mo.angle ?? z.area.facing;
      if (t) {
        const diff = wrapAngle(angleTo(z.area.origin, t.pos) - cur);
        const max = m.turnRate * DT;
        mo.angle = wrapAngle(cur + Math.max(-max, Math.min(max, diff)));
      }
      z.area.facing = mo.angle ?? cur;
      if (anchor?.alive) anchor.facing = z.area.facing;
      return;
    }
    case 'chase': {
      const t = unitOf(ctx, mo.targetId);
      if (!t?.alive) {
        mo.vel = { x: 0, y: 0 };
        return;
      }
      const d = dist(z.area.origin, t.pos);
      const step = Math.min(d, m.speed * DT);
      if (d < 1e-6) return;
      const ux = (t.pos.x - z.area.origin.x) / d;
      const uy = (t.pos.y - z.area.origin.y) / d;
      mo.vel = { x: ux * m.speed, y: uy * m.speed };
      z.area.origin = { x: z.area.origin.x + ux * step, y: z.area.origin.y + uy * step };
      return;
    }
    case 'grow': {
      const s = z.area.shape;
      if (s.type !== 'circle' || s.radius >= m.max) return;
      let rate = m.perSec;
      if (m.perOccupantSec) rate += m.perOccupantSec * ctx.sys.spatial.unitsInShape(ctx.world, z.area, { affects: z.affects, relativeTo: z.sourceId }).length;
      z.area.shape = { type: 'circle', radius: Math.min(m.max, s.radius + rate * DT) };
      return;
    }
  }
}

export function updateHazards(ctx: SimContext): void {
  for (const z of ctx.world.zones) if (z.motion && z.expiresAt > ctx.now) stepMotion(ctx, z, z.motion);
}
